import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { getJobs, getJobBySlug, postApplyJob } from "@/actions";
import { toast } from "react-toastify";
import { emailRegex } from "@/regex";

export const DEFAULT_QUESTIONS = [
  {
    title: "Name",
    type: "input",
    placeholder: "What's your full name",
    status: true,
  },
  {
    title: "Email",
    type: "input",
    placeholder: "Enter your email address",
    status: true,
  },
  {
    title: "Current location",
    type: "input",
    placeholder: "Current location",
    status: true,
  },
  {
    title: "Right to work",
    type: "radio",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    status: true,
  },
  {
    title: "What expertise of yours does the job profile match with you?",
    type: "editor",
    placeholder: "Write your answer here...",
    status: true,
  },
  {
    title: "What expertise although not mentioned but you think can add value to the organisation?",
    type: "editor",
    placeholder: "Write your answer here...",
    status: true,
  },
  {
    title: "What aspects of the job does not fall in your expertise but you are willing to learn?",
    type: "editor",
    placeholder: "Write your answer here...",
    status: true,
  },
  {
    title: "Please share relevant project summary and certifications that you think can help with better judging your candidacy",
    type: "file",
    status: true,
  },
  {
    title: "Upload your CV",
    type: "file",
    status: true,
  },
  {
    title: "Any comments or feedbacks for the recruiter?",
    type: "editor",
    placeholder: "Write your answer here...",
    status: true,
  },
  {
    title: "You agree to get your data processed under standard GDPR rules?",
    type: "radio",
    options: [
      { label: "Yes, I agree", value: "yes" },
      { label: "No, I don't", value: "no" },
    ],
    status: true,
  },
];

const useJobs = (initialJob = null) => {
  const router = useRouter();
  const { slug } = router.query;
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(initialJob);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({});

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getJobs();
      if (response?.responseCode === 1) {
        setJobs(response.data || []);
      } else {
        toast.error(response?.message || "Failed to fetch jobs");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong while fetching jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobBySlug = useCallback(async (slug) => {
    setLoading(true);
    try {
      const response = await getJobBySlug(slug);
      if (response?.responseCode === 1) {
        setJob(response.data || null);
      } else {
        toast.error(response?.message || "Failed to fetch job details");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong while fetching job details");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch job detail if slug is present
  useEffect(() => {
    if (router.isReady && slug && !initialJob) {
      fetchJobBySlug(slug);
    }
  }, [router.isReady, slug, fetchJobBySlug, initialJob]);

  // Form State
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  
  const questions = job?.questions?.length > 0 ? job.questions : DEFAULT_QUESTIONS;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (name) => (file) => {
    setFiles((prev) => ({ ...prev, [name]: file }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    questions.forEach(q => {
      if (q.status === false) return;

      if (q.type === 'file') {
        if (q.title === "Upload your CV" && !files[q.title]) {
           newErrors[q.title] = "Please upload your CV";
        }
      } else {
        const val = formData[q.title];
        if (q.title === "Name" && !val?.trim()) {
          newErrors[q.title] = "Full name is required";
        }
        else if (q.title === "Email") {
          if (!val?.trim()) newErrors[q.title] = "Email address is required";
          else if (!emailRegex.test(val)) newErrors[q.title] = "Please enter a valid email address";
        }
        else if (q.title === "Current location" && !val?.trim()) {
           newErrors[q.title] = "Current location is required";
        }
        else if (q.title === "Right to work" && !val) {
           newErrors[q.title] = "Please specify if you have the right to work";
        }
        else if (q.title === "You agree to get your data processed under standard GDPR rules?" && val !== "yes" && val !== "Yes, I agree") {
           newErrors[q.title] = "You must agree to the GDPR data processing rules to apply";
        }
        else if (q.title === "What expertise of yours does the job profile match with you?" && !val?.trim()) {
           newErrors[q.title] = "Please describe your expertise match";
        }
        else if (q.title === "What expertise although not mentioned but you think can add value to the organisation?" && !val?.trim()) {
           newErrors[q.title] = "Please describe what expertise can add value to the organisation";
        }
        else if (q.title === "What aspects of the job does not fall in your expertise but you are willing to learn?" && !val?.trim()) {
           newErrors[q.title] = "Please describe what you are willing to learn";
        }
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const errorElement = document.getElementsByName(firstErrorKey)[0];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e, currentJob, onSuccess) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("job_id", currentJob.id);
      
      const answers = {};

      questions.forEach(q => {
        if (q.status === false) return;
        
        const title = q.title;
        if (q.type === 'file') {
          if (files[title]) {
             if (title === "Upload your CV") data.append("cv", files[title]);
             else if (title === "Please share relevant project summary and certifications that you think can help with better judging your candidacy") data.append("project_summary", files[title]);
             else data.append(`file_${title}`, files[title]);
          }
        } else {
           const val = formData[title] || "";
           if (title === "Name") data.append("full_name", val);
           else if (title === "Email") data.append("email", val);
           else if (title === "Current location") data.append("current_location", val);
           else if (title === "Right to work") data.append("right_to_work", val);
           else if (title === "What expertise of yours does the job profile match with you?") data.append("expertise_match", val);
           else if (title === "What expertise although not mentioned but you think can add value to the organisation?") data.append("value_add", val);
           else if (title === "What aspects of the job does not fall in your expertise but you are willing to learn?") data.append("willing_to_learn", val);
           else if (title === "Any comments or feedbacks for the recruiter?") data.append("comments", val);
           else if (title === "You agree to get your data processed under standard GDPR rules?") {
              data.append("gdpr_agreement", (val === "yes" || val === "Yes, I agree") ? true : false);
           }
           else {
             answers[title] = val;
           }
        }
      });

      if (Object.keys(answers).length > 0) {
         data.append("answers", JSON.stringify(answers));
      }

      const response = await postApplyJob(data);
      if (response?.responseCode === 1) {
        toast.success(response.message || "Application submitted successfully!");
        if (onSuccess) onSuccess();
      } else {
        toast.error(response?.message || "Failed to submit application");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong while submitting application");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    jobs,
    job,
    loading,
    submitting,
    questions,
    formData,
    files,
    errors,
    setFormData,
    setFiles,
    fetchJobs,
    fetchJobBySlug,
    handleChange,
    handleFileChange,
    handleSubmit,
  };
};

export default useJobs;
